import {create} from 'zustand';
import {immer} from 'zustand/middleware/immer';

import type {HydrationResult} from 'src/components/analytics-genesis/byod/store/hydrateDashboard';

// ---------------------------------------------------------------------------
// dashboardViewStore — per-dashboard state for the embeddable, read-only
// <ByodDashboardView>, keyed by dashboard id.
// ---------------------------------------------------------------------------
// Distinct from the builder's single-dashboard `dashboardStore`: the view is
// multi-instance (N embeds can render on one page), so its state is a MAP
// `byId[dashboardId] -> entry` rather than one live dashboard. Two embeds of the
// same id share one entry (dedup); two embeds of different ids stay isolated by
// key. Per-instance inputs that are NOT intrinsic to the dashboard — the date
// window and the injected `globalPayload`/filters — are deliberately NOT stored
// here (they'd clash across instances of the same id); they flow to <Canvas> as
// props. Server fetch state (loading/error/refetch) stays in TanStack Query
// (`useLoadDashboard`), which is itself keyed by id; this store holds only the
// hydrated, app-shaped dashboard DATA derived from a successful load.

/**
 * Per-tile `widget_config_json` patches applied LOCALLY by the viewer (e.g.
 * column show/hide via the table's "Edit table columns" picker), keyed by tile
 * id. Session-only — never written back to the backend. Lives here (not in tile
 * component state) so it's scoped to the dashboard and survives tile remounts /
 * scroll-out / tab switches within the session.
 */
export type WidgetOverrides = Record<string, Record<string, unknown>>;

export type DashboardViewEntry = {
  /**
   * Hydrated dashboard data (rowOrder / rows / widgets / orphanedWidgetCount),
   * or `null` until the dashboard's `/dashboards/:id` load has been hydrated.
   */
  hydrated: HydrationResult | null;
  /**
   * True when a load succeeded but hydration threw (a malformed payload — a
   * parse failure, not a network one). Renders a distinct, non-retryable state.
   */
  hydrationFailed: boolean;
  /** Viewer's local, session-only per-tile config overrides (see above). */
  widgetOverrides: WidgetOverrides;
};

/**
 * Stable empty entry returned for an unseen dashboard id, so the selector hook
 * hands back a referentially-constant value (no render loop) before the first
 * hydration writes a real entry.
 */
export const EMPTY_DASHBOARD_VIEW_ENTRY: DashboardViewEntry = {
  hydrated: null,
  hydrationFailed: false,
  widgetOverrides: {},
};

/** Stable empty overrides map for the not-yet-seen id case (no render loop). */
const EMPTY_OVERRIDES: WidgetOverrides = {};

interface DashboardViewState {
  /** Per-dashboard view state, keyed by dashboard id. */
  byId: Record<string, DashboardViewEntry>;
  /** Store the successfully-hydrated data for a dashboard id. */
  setHydrated: (dashboardId: string, hydrated: HydrationResult) => void;
  /** Flag a load that succeeded but failed to hydrate for a dashboard id. */
  setHydrationFailed: (dashboardId: string) => void;
  /** Merge a session-only `widget_config` patch for one tile of a dashboard. */
  setWidgetOverride: (
    dashboardId: string,
    tileId: string,
    patch: Record<string, unknown>,
  ) => void;
}

export const useDashboardViewStore = create<DashboardViewState>()(
  immer((set) => ({
    byId: {},
    // Preserve any local widgetOverrides across (re)hydration — they're the
    // viewer's session state, independent of the reloaded dashboard data.
    setHydrated: (dashboardId, hydrated) =>
      set((s) => {
        s.byId[dashboardId] = {
          hydrated,
          hydrationFailed: false,
          widgetOverrides: s.byId[dashboardId]?.widgetOverrides ?? {},
        };
      }),
    setHydrationFailed: (dashboardId) =>
      set((s) => {
        s.byId[dashboardId] = {
          hydrated: null,
          hydrationFailed: true,
          widgetOverrides: s.byId[dashboardId]?.widgetOverrides ?? {},
        };
      }),
    setWidgetOverride: (dashboardId, tileId, patch) =>
      set((s) => {
        const entry =
          s.byId[dashboardId] ??
          (s.byId[dashboardId] = {
            hydrated: null,
            hydrationFailed: false,
            widgetOverrides: {},
          });
        entry.widgetOverrides[tileId] = {
          ...(entry.widgetOverrides[tileId] ?? {}),
          ...patch,
        };
      }),
  })),
);

/**
 * Read a single dashboard's view entry by id. Returns a stable empty entry
 * (never `undefined`) for an id that hasn't been hydrated yet, so callers can
 * destructure `{hydrated, hydrationFailed}` unconditionally.
 */
export function useDashboardViewEntry(dashboardId: string): DashboardViewEntry {
  return useDashboardViewStore(
    (s) => s.byId[dashboardId] ?? EMPTY_DASHBOARD_VIEW_ENTRY,
  );
}

/**
 * Read a dashboard's local per-tile config overrides. Returns a stable empty
 * map for an id with no overrides yet, so callers can use it in a memo dep
 * without churning.
 */
export function useDashboardViewOverrides(
  dashboardId: string,
): WidgetOverrides {
  return useDashboardViewStore(
    (s) => s.byId[dashboardId]?.widgetOverrides ?? EMPTY_OVERRIDES,
  );
}
